import {
  type CompanySelectDto,
  type LlmLogSelectDto,
  type LlmLogStatus,
  type LlmLogType,
  LlmRole,
  type UserSelectDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { Spinner } from '@components/shared/spinner.component';
import { Badge, BadgeProps } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { format } from 'date-fns';
import { CheckIcon, CopyIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LLM_LOG_TYPE_LABELS } from '../../utils/constants';

type LlmLogModalDetailsProps = {
  isLoading?: boolean;
  isOpen: boolean;
  log?: LlmLogSelectDto;
  onClose: () => void;
};

type SummaryItem = {
  colSpan?: string;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
};

const statusVariantMap: Record<LlmLogStatus, BadgeProps['variant']> = {
  aborted: 'warning',
  completed: 'success',
  failed: 'destructive',
  pending: 'pending',
};

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  return format(new Date(value), 'PPP p');
};

const formatDuration = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  if (value < 1000) {
    return `${Math.round(value)} ms`;
  }

  const seconds = value / 1000;
  return `${seconds.toFixed(seconds < 10 ? 2 : 1)} s`;
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat().format(value);
};

const formatType = (value?: LlmLogType) => {
  if (!value) return '—';
  if (value in LLM_LOG_TYPE_LABELS) {
    return LLM_LOG_TYPE_LABELS[value];
  }

  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const formatJson = (value: unknown) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const hasRenderableContent = (value: unknown) => {
  if (value === null || value === undefined) return false;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return true;
};

const JsonBlock: React.FC<{
  copyLabel: string;
  data?: unknown;
  title: string;
  onCopy: (label: string, payload: unknown) => Promise<void>;
  copiedStates: Set<string>;
}> = ({ copyLabel, data, title, onCopy, copiedStates }) => {
  const content = formatJson(data);
  const trimmedContent = content.trim();
  const hasContent = hasRenderableContent(data) && trimmedContent.length > 0 && !['{}', '[]'].includes(trimmedContent);
  const isCopied = copiedStates.has(copyLabel);

  return (
    <section className="relative">
      <header className="flex items-center justify-between sticky -top-4 py-1.5 bg-white z-10 gap-2">
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
        {hasContent && (
          <Button
            aria-label="Copy All Messages"
            className="gap-2 text-xs"
            onClick={() => onCopy(copyLabel, data ?? '')}
            size="sm"
            variant="secondary"
          >
            {copiedStates.has(copyLabel) ? (
              <>
                <CheckIcon className="size-4" /> Copied
              </>
            ) : (
              <>
                <CopyIcon className="size-4" /> Copy
              </>
            )}
          </Button>
        )}
      </header>

      <div className="group relative">
        <pre
          className={cn(
            'rounded-lg border bg-gray-50 p-3 text-xs leading-relaxed text-gray-800 max-h-80 overflow-auto whitespace-pre-wrap wrap-break-word',
            !hasContent && 'italic text-gray-500',
          )}
        >
          {hasContent ? content : 'No data available.'}
        </pre>
      </div>
    </section>
  );
};

const ConversationBlock: React.FC<{
  messages: LlmLogSelectDto['messages'];
  onCopy: (label: string, payload: unknown) => Promise<void>;
  copiedStates: Set<string>;
}> = ({ messages, onCopy, copiedStates }) => {
  const hasMessages = messages.length > 0;

  const terminalText = hasMessages
    ? messages.map((m, idx) => `#${idx + 1} [${m.role.toUpperCase()}]\n${m.content}`).join('\n\n')
    : '';

  return (
    <section className="space-y-2 relative">
      <header className="flex items-center justify-between sticky -top-4 py-1.5 bg-white z-10 gap-2">
        <h4 className="text-sm font-medium text-gray-700">Request</h4>
        {hasMessages && (
          <Button
            aria-label="Copy All Messages"
            className="gap-2 text-xs"
            onClick={() => onCopy('All Messages', terminalText)}
            size="sm"
            variant="secondary"
          >
            {copiedStates.has('All Messages') ? (
              <>
                <CheckIcon className="size-4" /> Copied
              </>
            ) : (
              <>
                <CopyIcon className="size-4" /> Copy All Messages
              </>
            )}
          </Button>
        )}
      </header>

      {hasMessages ? (
        <div className="space-y-3">
          {messages.map((message, index) => {
            const messageLabel = `Message ${index + 1}`;
            const isCopied = copiedStates.has(messageLabel);

            return (
              <div
                key={index}
                className="group relative rounded-lg border bg-gray-50 p-1"
              >
                <div className="flex items-center justify-between mb-2 pt-2 px-1">
                  <div className="text-xs flex items-center gap-1 uppercase tracking-wide font-medium text-gray-500">
                    #{index + 1}{' '}
                    <Badge
                      variant={message.role === LlmRole.User ? 'primary' : 'secondary'}
                      size="xs"
                    >
                      {message.role === LlmRole.User ? 'User Prompt' : message.role}
                    </Badge>
                  </div>
                  <IconButton
                    ariaLabel={`Copy ${messageLabel}`}
                    className="border border-gray-200"
                    icon={isCopied ? <CheckIcon /> : <CopyIcon />}
                    onClick={() => onCopy(messageLabel, message.content)}
                    size="sm"
                    variant="secondary"
                  />
                </div>
                <div className="max-h-60 overflow-auto rounded-md border bg-white p-3">
                  <pre className="whitespace-pre-wrap wrap-break-word font-mono text-xs leading-relaxed text-gray-800">
                    {message.content}
                  </pre>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-gray-50 p-3 text-xs italic text-gray-500">No messages captured.</div>
      )}
    </section>
  );
};

function UserInfoCard(props: {
  user: UserSelectDto;
  isCopied: boolean;
  onCopy: (label: string, payload: unknown) => Promise<void>;
}) {
  const { user, isCopied, onCopy } = props;
  return (
    <div className="group relative flex flex-col gap-1 rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">User</span>
        <IconButton
          ariaLabel="Copy User"
          className="hidden absolute top-1 right-1 border border-gray-200 group-hover:flex"
          icon={isCopied ? <CheckIcon /> : <CopyIcon />}
          onClick={() =>
            onCopy('User', {
              name: user?.profile?.fullName,
              email: user?.email,
              id: user?.id,
            })
          }
          size="sm"
          variant="secondary"
        />
      </div>
      <span className="text-gray-700">{user?.profile?.fullName || '—'}</span>
      {user?.email && <span className="text-xs text-gray-500">{user.email}</span>}
      <span className="break-all text-xs text-gray-500">ID - {user?.id ?? '—'}</span>
    </div>
  );
}

function CompanyInfoCard(props: {
  company: CompanySelectDto;
  isCopied: boolean;
  onCopy: (label: string, payload: unknown) => Promise<void>;
}) {
  const { company, isCopied, onCopy } = props;
  return (
    <div className="group relative flex flex-col gap-1 rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">Company</span>
        <IconButton
          ariaLabel="Copy Company"
          className="absolute top-1 right-1 hidden border border-gray-200 group-hover:flex"
          disabled={!company?.name}
          icon={isCopied ? <CheckIcon /> : <CopyIcon />}
          onClick={() =>
            onCopy('Company', {
              id: company?.id,
              name: company?.name,
            })
          }
          size="sm"
          variant="secondary"
        />
      </div>
      <span className="text-gray-700">{company?.name ?? '—'}</span>
      <span className="break-all text-xs mt-auto text-gray-500">ID - {company?.id ?? '—'}</span>
    </div>
  );
}

function TechnologyInfoCard(props: {
  technology: LlmLogSelectDto['technology'];
  isCopied: boolean;
  onCopy: (label: string, payload: unknown) => Promise<void>;
}) {
  const { technology, isCopied, onCopy } = props;
  return (
    <div className="group relative flex flex-col gap-1 rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">Technology</span>

        <IconButton
          ariaLabel="Copy Technology"
          className="absolute top-1 right-1 hidden border border-gray-200 group-hover:flex"
          disabled={!technology?.name}
          icon={isCopied ? <CheckIcon /> : <CopyIcon />}
          onClick={() =>
            onCopy('Technology', {
              id: technology?.id,
              name: technology?.name,
              category: technology?.category?.name,
              subcategory: technology?.subcategory?.name,
            })
          }
          size="sm"
          variant="secondary"
        />
      </div>

      <span className="text-gray-700">{technology?.name ?? '—'}</span>
      <div className="items-center gap-2 text-xs text-gray-500">
        Category: {technology?.category?.name && <span>{technology.category.name}</span>}
      </div>
      <span className="break-all text-xs mt-auto text-gray-500">ID - {technology?.id ?? '—'}</span>
    </div>
  );
}

export const LlmLogModalDetails: React.FC<LlmLogModalDetailsProps> = (props) => {
  const { isLoading = false, isOpen, log, onClose } = props;

  // Track which copy buttons are in "copied" state
  const [copiedStates, setCopiedStates] = useState<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleCopy = useCallback(async (label: string, payload: unknown) => {
    if (payload === null || payload === undefined) {
      errorToast({ description: `No ${label.toLowerCase()} available to copy.` });
      return;
    }

    const text = typeof payload === 'string' ? payload : formatJson(payload);

    const trimmedText = text.trim();
    if (trimmedText.length === 0 || ['{}', '[]'].includes(trimmedText)) {
      errorToast({ description: `No ${label.toLowerCase()} available to copy.` });
      return;
    }

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);

        // Clear any existing timeout for this label
        const existingTimeout = timeoutsRef.current.get(label);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Set the copied state
        setCopiedStates((prev) => new Set(prev).add(label));

        // Set timeout to reset the copied state after 5 seconds
        const timeout = setTimeout(() => {
          setCopiedStates((prev) => {
            const newSet = new Set(prev);
            newSet.delete(label);
            return newSet;
          });
          timeoutsRef.current.delete(label);
        }, 3000);

        timeoutsRef.current.set(label, timeout);
      } else {
        errorToast({ description: 'Clipboard not supported in this browser.' });
      }
    } catch {
      errorToast({ description: `Unable to copy ${label.toLowerCase()}.` });
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const summaryItems = useMemo<SummaryItem[]>(() => {
    if (!log) return [];
    const { metadata } = log;
    const {
      actualModel,
      cachedInputTokens,
      durationMs,
      finishReason,
      inputTokens,
      outputTokens,
      requestedModel,
      templateKey,
      templateVersion,
      timeToFirstTokenMs,
      totalTokens,
    } = metadata ?? {};

    const hasActualModel = Boolean(actualModel && requestedModel !== actualModel);

    return [
      // { label: 'Log ID', value: log.id, valueClassName: 'break-all text-xs text-gray-600' },
      { label: 'Requested At', value: formatDateTime(log.createdAt) },
      { label: 'Type', value: formatType(log.type) },
      { label: 'Template Key', value: templateKey ?? '—', valueClassName: 'break-all text-xs' },
      {
        label: 'Version',
        value: templateVersion === null || templateVersion === undefined ? '—' : `v${templateVersion}`,
      },
      {
        label: `Model ${hasActualModel ? '(requested / used)' : ''}`,
        value: `${requestedModel} ${hasActualModel ? `— ${actualModel}` : ''}`,
        valueClassName: 'text-xs',
      },
      {
        label: 'Status',
        value: (
          <Badge
            size="xs"
            variant={statusVariantMap[log.status] ?? 'default'}
          >
            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
          </Badge>
        ),
      },
      {
        label: 'Tokens (Input / Output / Total)',
        value: `${formatNumber(inputTokens ?? cachedInputTokens)} / ${formatNumber(outputTokens)} / ${formatNumber(totalTokens)}`,
      },
      { label: 'Finish Reason', value: finishReason ?? '—' },
      {
        label: 'Duration / Latency',
        value: `${formatDuration(durationMs)} / ${formatDuration(timeToFirstTokenMs)}`,
      },
      {
        label: 'Approval',
        value:
          log.accepted === true ? (
            <Badge
              variant="success"
              size="xs"
            >
              Accepted
            </Badge>
          ) : log.accepted === false ? (
            <Badge
              variant="destructive"
              size="xs"
            >
              Rejected
            </Badge>
          ) : (
            '—'
          ),
      },
    ];
  }, [log]);

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      width="70%"
      header={{
        title: 'LLM Log Details',
        subtitle: (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span>{log?.id}</span>
            <IconButton
              ariaLabel="Copy Log ID"
              className=""
              icon={copiedStates.has('Log ID') ? <CheckIcon /> : <CopyIcon />}
              onClick={() =>
                handleCopy('Log ID', {
                  id: log?.id,
                })
              }
              size="sm"
              variant="secondary"
            />
          </div>
        ),
      }}
      contentClassname="space-y-2"
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : log ? (
        <>
          <section className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {log.user && (
              <UserInfoCard
                user={log.user}
                isCopied={copiedStates.has('User')}
                onCopy={handleCopy}
              />
            )}

            {log.user?.companies?.[0] && (
              <CompanyInfoCard
                company={log.user?.companies?.[0]}
                isCopied={copiedStates.has('Company')}
                onCopy={handleCopy}
              />
            )}

            {log.technology && (
              <TechnologyInfoCard
                technology={log.technology}
                isCopied={copiedStates.has('Technology')}
                onCopy={handleCopy}
              />
            )}
          </section>

          <section className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {summaryItems.map((item, index) => {
              const isStringValue = typeof item.value === 'string';
              const stringValue = isStringValue ? (item.value as string).trim() : '';
              const isEmptyString = isStringValue && (stringValue.length === 0 || stringValue === '—');

              return (
                <div
                  key={`${item.label}-${index}`}
                  className={cn(
                    'group relative flex flex-col gap-0 rounded-lg border bg-gray-50 px-2 py-1.5 text-sm text-gray-700',
                    item.colSpan,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500">{item.label}</span>
                    {isStringValue && !isEmptyString && (
                      <IconButton
                        ariaLabel={`Copy ${item.label}`}
                        className="hidden absolute top-1 right-1 border border-gray-200 group-hover:flex"
                        icon={copiedStates.has(item.label) ? <CheckIcon /> : <CopyIcon />}
                        onClick={() => handleCopy(item.label, `${item.label}: ${item.value}`)}
                        size="sm"
                        variant="secondary"
                      />
                    )}
                  </div>

                  <span className={cn('text-gray-700', item.valueClassName, isEmptyString && 'text-gray-500')}>
                    {item.value}
                  </span>
                </div>
              );
            })}
          </section>

          <div className="pt-4 grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <ConversationBlock
                messages={log.messages}
                onCopy={handleCopy}
                copiedStates={copiedStates}
              />
              <JsonBlock
                copyLabel="Raw request"
                data={log.rawRequest}
                onCopy={handleCopy}
                title="Raw Request"
                copiedStates={copiedStates}
              />

              <JsonBlock
                copyLabel="Metadata"
                data={log.metadata}
                onCopy={handleCopy}
                title="Metadata"
                copiedStates={copiedStates}
              />
            </div>

            <div className="space-y-4">
              <JsonBlock
                copyLabel="Response"
                data={log.result && 'text' in log.result ? log.result.text : log.result || {}}
                onCopy={handleCopy}
                title="Response"
                copiedStates={copiedStates}
              />

              <JsonBlock
                copyLabel="Raw response"
                data={log.rawResponse}
                onCopy={handleCopy}
                title="Raw Response"
                copiedStates={copiedStates}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="py-10 text-center text-sm text-muted-foreground">Select an interaction to see the details.</div>
      )}
    </Modal>
  );
};
